/**
 * 이상한 방 - 세션의 리빌 배선 (재생기 + 증거 표시)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 5절(웃김 설계 포인트 6번)
 *
 * `session.ts`에서 갈라져 나왔다. 연출의 내용은 `game/reveal.ts`(순서·카메라)와
 * `render/revealHighlight.ts`(금빛 표시)가 쥐고 있고, 여기는 그 둘을 **한 라운드의 수명에
 * 묶어 두는 배선**이다 - 세우고, 프레임마다 밀고, 라운드가 끝나면 반드시 걷는다.
 *
 * ## 리빌은 방을 갈아 끼우지 않는다
 *
 * 리빌의 깜빡 교차(0.35초 간격 원본↔변경본)는 **motion transient를 일부러 쓰는** 연출이다.
 * 수색 중에는 치트인 것이 리빌에서는 정답이므로(`reveal.ts` 상단 주석) 여기서는 디졸브를 쓰지 않는다.
 *
 * 다만 **뒤집는 단위가 방에서 한 건으로 바뀌었다.** 직전까지 `onRevealStep`이
 * `renderRoom(원본 또는 변경본)`으로 방을 통째로 갈아 끼웠는데, 카메라가 한 건에 붙어 있는
 * 동안 화면 구석의 나머지 열한 개까지 같이 뒤집혔다. 깜빡비교의 원리가 "한 곳만 달라지는 두 장"인데
 * 열두 곳이 동시에 달라지면 시각계가 어느 것도 집어내지 못한다 - 연출이 자기 목적을 지운 것이다.
 *
 * 그래서 씬은 리빌 내내 **변경본으로 고정**이고(그 고정은 부르는 쪽이 한다), 포커스된 한 건만
 * `render/revealHighlight`가 뒤집으며 금빛 증거 표시로 그것을 짚는다. 이 파일이 하는 일은
 * 배선 셋뿐이다 - 스텝이 바뀔 때 `focus`, 깜빡일 때 `setSide`, 프레임마다 `update`.
 *
 * ## 스텝 목록은 재생기보다 오래 산다
 *
 * `stop()`은 재생기와 증거 표시만 접고 **스텝 목록은 남긴다.** 결과 화면의 "놓친 변화"가
 * 그것을 읽기 때문이고, 리빌 → 결과 전환에서 재생기는 반드시 먼저 접혀야 한다
 * (금빛이 남으면 결과 화면 배경에 정답 하나가 계속 표시된다). 목록까지 버리는 것은
 * 새 라운드가 설 때뿐이라 문이 둘이다(`stop` / `clear`).
 */

import type { FloorPlan } from '../core/floorPlan'
import type { Catalog } from '../core/room'
import type { IsoCamera } from '../input/isoCamera'
import { createRevealHighlight, type RevealHighlight } from '../render/revealHighlight'
import type { SceneCtx } from '../render/scene'
import type { NoticeTone } from '../ui/gameHud'
import {
  buildRevealSequence,
  createRevealPlayer,
  type RevealPlayer,
  type RevealSequenceOptions,
  type RevealSource,
  type RevealStep,
  type RevealView,
} from './reveal'

export type SessionRevealOptions = Readonly<{
  ctx: SceneCtx
  iso: IsoCamera
  catalog: Catalog
  plan: FloorPlan
  /** 한 스텝의 길이(초). `tuning.revealSecondsPerChange`가 그대로 들어온다. */
  secondsPerStep: number
  /**
   * 스텝이 바뀔 때의 라벨 한 줄. HUD 토스트로 나간다.
   * 깜빡일 때가 아니라 **스텝이 바뀔 때만** 불린다 - 초당 세 번 재시작되는 토스트는 읽을 수 없다.
   */
  showLabel: (text: string, tone: NoticeTone) => void
  /** 스텝마다 셔터 한 번. 라벨과 같은 조건에 묶여 있다. */
  playStepSound: () => void
  /**
   * 스텝이 바뀔 때 한 번 더(여럿이, 로드맵 M5). 라벨·소리와 **같은 조건**에서 불린다 - 깜빡일 때가 아니라 스텝 진입에만.
   * 파티 리빌이 이름표("OO이 찾았다")와 "아무도 못 봤다" 스탬프를 여기서 세운다(`ui/revealParty.ts`). 싱글은 넘기지 않는다 -
   * 그 자리는 라벨 하나로 충분하고, 없는 콜백은 부르지 않는다.
   */
  onStepEnter?: (step: RevealStep, index: number, steps: readonly RevealStep[]) => void
  /** 발견분까지 다 돌았다. 남는 초는 전부 손해다(8절). */
  onFinished: () => void
}>

export type SessionReveal = Readonly<{
  /**
   * 이 라운드의 리빌을 세운다. 이전 것이 남아 있으면 먼저 접는다.
   *
   * 받는 것은 `GameState`가 아니라 그중 리빌이 읽는 넷(`RevealSource`)이다 - 매치 세션이 `rooms` 메시지의 두 방과
   * 뷰의 정답표·발견 목록을 같은 모양으로 넘겨 **같은 재생기**를 돈다(로드맵 M2 "리빌은 클라이언트 주도 재생기 그대로").
   *
   * @param reducedMotion 움직임 줄이기. **부르는 쪽이 그때그때 물어서 넘긴다** -
   *                      캐시하면 OS 설정을 켜도 새로고침 전까지 안 먹는다.
   * @param sequence      순서·이름표 옵션(`reveal.RevealSequenceOptions`). 싱글은 넘기지 않는다 - 그러면 마지막 자리가 가장 교묘한
   *                      것이고 스텝에 `foundBy`가 없다. 여럿이는 `matchSessionReveal.partyRevealSequence`가 만든 것을 넘긴다.
   */
  start: (state: RevealSource, reducedMotion: boolean, sequence?: RevealSequenceOptions) => void
  /** 한 프레임 진행(초). 재생기와 증거 표시를 함께 민다. */
  update: (dtSeconds: number) => void
  /** 이번 라운드의 스텝 목록. 재생기를 접은 뒤에도 살아 있다(결과 화면이 읽는다). */
  steps: () => readonly RevealStep[]
  /**
   * 금빛 표시만 즉시 걷는다. **치명적 오류 경로용**이다 - 그때 하이라이트는 아직 살아 있고,
   * 포커스된 한 건이 원본 모습으로 선 채 결과 화면 배경에 정답 하나가 계속 표시된다.
   * 어디서 불러도 안전하고, 이미 걷힌 뒤라면 아무 일도 하지 않는다.
   */
  clearHighlight: () => void
  /** 재생기와 증거 표시를 접는다. **스텝 목록은 남는다.** */
  stop: () => void
  /** `stop()` + 스텝 목록까지 버린다. 새 라운드가 설 때. */
  clear: () => void
}>

export function createSessionReveal(options: SessionRevealOptions): SessionReveal {
  const { ctx, iso, catalog, plan } = options

  let steps: readonly RevealStep[] = []
  let player: RevealPlayer | null = null
  /** 포커스된 한 건만 뒤집는 증거 표시. 리빌 페이즈에만 살아 있다(수색 중이면 곧바로 정답 유출이다). */
  let highlight: RevealHighlight | null = null
  let index = -1

  function stop(): void {
    player?.dispose()
    player = null
    // 씬에서 자기 노드를 걷어내고 머티리얼 참조를 원상복구한다. 빠뜨리면 결과 화면 배경에 금빛이 남는다.
    highlight?.dispose()
    highlight = null
    index = -1
  }

  /** 스텝 리스너. 깜빡 교차로 화면이 바뀔 때마다 불리므로 **여기서 무거운 일을 하면 안 된다.** */
  function onStep(step: RevealStep, view: RevealView, at: number): void {
    /*
     * **방을 갈아 끼우지 않는다.** 씬은 리빌 내내 변경본으로 고정이고, 포커스된 한 건만
     * `highlight`가 뒤집는다. 여기서 방을 통째로 되돌리면 화면 구석의 다른 열한 개까지
     * 같이 깜빡여 깜빡비교가 자기 목적을 지운다(머리말 참고).
     */
    if (at !== index) {
      index = at
      // `setSide`보다 **먼저** 와야 한다. `focus`가 대상을 'mutated'로 초기화하기 때문이다.
      highlight?.focus(step.change.targetId)
      // 라벨은 스텝이 바뀔 때만. 깜빡일 때마다 띄우면 토스트가 초당 세 번 재시작돼 읽을 수 없다.
      options.showLabel(step.label, step.found ? 'good' : 'warn')
      /*
       * 스텝마다 셔터 한 번. 리빌은 이 게임의 클라이맥스인데(컨셉시트 5절) 화면만 깜빡이면
       * 그 순간이 정지 화면처럼 읽힌다. 소리를 라벨과 같은 조건에 묶어 두는 것이 중요하다 -
       * 깜빡임에 붙이면 초당 세 번 울려 그 자체가 소음이 된다.
       */
      options.playStepSound()
      options.onStepEnter?.(step, at, steps)
    }

    // `RevealView`와 `RevealSide`는 유니온이 같아서 변환 없이 그대로 들어간다.
    highlight?.setSide(view)
  }

  return {
    start(state: RevealSource, reducedMotion: boolean, sequence: RevealSequenceOptions = {}): void {
      stop()

      /*
       * 씬은 여기서부터 리빌이 끝날 때까지 **변경본으로 고정**이다. 뒤집히는 것은
       * 포커스된 한 건뿐이고 그 일은 전부 이 물건이 한다.
       *
       * `stop()`이 맨 앞에 있으므로 라운드마다 새로 만들어지고 이전 것은 정리된다.
       */
      highlight = createRevealHighlight({
        ctx,
        catalog,
        original: state.original,
        mutated: state.mutated,
        // 진동만 끄고 표시는 고정값으로 남는다. 표시를 끄면 어디를 보라는 정보가 통째로 사라진다.
        reducedMotion,
      })

      steps = buildRevealSequence(state, catalog, plan, sequence)

      /*
       * **`budgetSeconds`를 주지 않는다.** 재생기는 전체 길이를 예산에 맞춰 **모든 스텝을 같은 비율로**
       * 줄이는데, 시퀀스에는 미발견분 뒤에 발견분 훑기까지 들어 있다(`reveal.ts`).
       * 반면 리빌 페이즈 길이는 `phaseDurationMs`가 **미발견 수 × 스텝 시간**으로만 잡는다.
       *
       * 그래서 예산을 넘기면 12개 중 3개만 못 찾은 판에서 7.5초를 12스텝이 나눠 갖게 되어
       * 한 장이 0.6초가 된다. 실제로 그렇게 돌려 봤고, "저걸 못 봤다고???"가 나올 틈이
       * 물리적으로 없었다 - 컨셉시트 5절이 리빌에 부여한 유일한 역할이 사라진 것이다.
       *
       * 예산 없이 두면 **미발견분이 앞에 있으므로 항상 온전한 길이로 재생되고**,
       * 페이즈 타이머가 끝나는 순간 잘리는 것은 뒤쪽의 발견분 훑기(보너스)뿐이다.
       * 잘려도 되는 쪽이 잘리는 배치이며, 그래서 순서가 곧 우선순위다.
       */
      player = createRevealPlayer(iso, steps, onStep, options.onFinished, {
        secondsPerStep: options.secondsPerStep,
      })
    },

    update(dtSeconds: number): void {
      player?.update(dtSeconds)
      highlight?.update(dtSeconds)
    },

    steps: () => steps,
    clearHighlight: () => highlight?.clear(),
    stop,

    clear(): void {
      stop()
      steps = []
    },
  }
}
